import { Button, SelectInput, TextInput, type SelectInputOption } from '@my/ui';
import {
  TARGET_FIELDS,
  type ColumnMapping,
  type ImportDefaults,
  type ParsedFile,
  type TargetField,
  type TargetFieldDef,
} from '../lib/types';

interface MappingStepProps {
  parsed: ParsedFile;
  mapping: ColumnMapping;
  defaults: ImportDefaults;
  onMappingChange: (next: ColumnMapping) => void;
  onDefaultsChange: (next: ImportDefaults) => void;
  onBack: () => void;
  onContinue: () => void;
}

const IGNORE = '__ignore__';

/**
 * Step 2 — map source columns to target fields, and pick the batch-level
 * defaults (party type, payment terms, currency rule). Required targets
 * (Name / Address / Country) must be mapped before continuing; Code is
 * optional (blank → auto-generated at commit).
 */
export function MappingStep({
  parsed,
  mapping,
  defaults,
  onMappingChange,
  onDefaultsChange,
  onBack,
  onContinue,
}: MappingStepProps) {
  const headerOptions: SelectInputOption[] = [
    { value: IGNORE, label: '— ignore —' },
    ...parsed.headers.map((h) => ({ value: h, label: h })),
  ];

  const setField = (field: TargetField, value: string) => {
    const next: ColumnMapping = { ...mapping };
    if (value === IGNORE || value === '') delete next[field];
    else next[field] = value;
    onMappingChange(next);
  };

  // Sample value for a mapped field from the first row — gives the operator a
  // live "this column → this value" confirmation while mapping.
  const sample = (field: TargetField): string | null => {
    const header = mapping[field];
    if (!header) return null;
    const v = parsed.rows[0]?.[header];
    return v && v.trim() ? v.trim() : null;
  };

  const requiredMissing = TARGET_FIELDS.filter((f) => f.required && !mapping[f.id]).map(
    (f) => f.label,
  );
  const canContinue = requiredMissing.length === 0;

  const partyFields = TARGET_FIELDS.filter((f) => f.group === 'party');
  const addressFields = TARGET_FIELDS.filter((f) => f.group === 'address');

  const renderRow = (f: TargetFieldDef) => {
    const s = sample(f.id);
    return (
      <div key={f.id} className="grid grid-cols-1 items-start gap-2 sm:grid-cols-[1fr_1.2fr]">
        <div className="pt-2">
          <span className="text-sm font-medium text-slate-700">{f.label}</span>
          {f.required && <span className="ml-0.5 text-red-500">*</span>}
          {f.hint && <p className="text-[11px] italic text-slate-400">{f.hint}</p>}
        </div>
        <div>
          <SelectInput
            label="Source column"
            name={`map-${f.id}`}
            value={mapping[f.id] ?? IGNORE}
            options={headerOptions}
            onChange={(e) => setField(f.id, e.target.value)}
          />
          {s && (
            <p className="ml-1 mt-0.5 truncate text-[11px] text-slate-500" title={s}>
              e.g. <span className="font-mono">{s}</span>
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {/* Batch defaults */}
      <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Applies to every imported row
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <SelectInput
            label="Type"
            name="def-type"
            value={defaults.type}
            options={[
              { value: 'Customer', label: 'Customer' },
              { value: 'Supplier', label: 'Supplier' },
              { value: 'Both', label: 'Both' },
            ]}
            onChange={(e) =>
              onDefaultsChange({ ...defaults, type: e.target.value as ImportDefaults['type'] })
            }
          />
          <SelectInput
            label="Payment method"
            name="def-payment"
            value={defaults.paymentMethod}
            options={[
              { value: 'Credit', label: 'Credit' },
              { value: 'Cash', label: 'Cash' },
              { value: 'SelfBill', label: 'Self bill' },
              { value: 'Internal', label: 'Internal' },
            ]}
            onChange={(e) =>
              onDefaultsChange({
                ...defaults,
                paymentMethod: e.target.value as ImportDefaults['paymentMethod'],
              })
            }
          />
          <SelectInput
            label="Billing frequency"
            name="def-frequency"
            value={defaults.billingFrequency}
            options={[
              { value: 'Weekly', label: 'Weekly' },
              { value: 'Monthly', label: 'Monthly' },
              { value: 'Quarterly', label: 'Quarterly' },
              { value: 'Yearly', label: 'Yearly' },
            ]}
            onChange={(e) =>
              onDefaultsChange({
                ...defaults,
                billingFrequency: e.target.value as ImportDefaults['billingFrequency'],
              })
            }
          />
          <SelectInput
            label="Status"
            name="def-status"
            value={defaults.status}
            options={[
              { value: 'Active', label: 'Active' },
              { value: 'Suspended', label: 'Suspended' },
              { value: 'CeasedTrading', label: 'Ceased trading' },
            ]}
            onChange={(e) =>
              onDefaultsChange({ ...defaults, status: e.target.value as ImportDefaults['status'] })
            }
          />
          <SelectInput
            label="Currency"
            name="def-currency-mode"
            value={defaults.currencyMode}
            options={[
              { value: 'fromCountry', label: 'From country (IE→EUR, GB/XI→GBP)' },
              { value: 'fixed', label: 'Fixed for all rows' },
            ]}
            onChange={(e) =>
              onDefaultsChange({
                ...defaults,
                currencyMode: e.target.value as ImportDefaults['currencyMode'],
              })
            }
          />
          {defaults.currencyMode === 'fixed' && (
            <TextInput
              label="Currency (ISO)"
              name="def-fixed-currency"
              value={defaults.fixedCurrency}
              maxLength={3}
              onChange={(e) =>
                onDefaultsChange({ ...defaults, fixedCurrency: e.target.value.toUpperCase() })
              }
            />
          )}
        </div>
      </div>

      {/* Column mapping */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Party</p>
          {partyFields.map(renderRow)}
        </div>
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Address</p>
          {addressFields.map(renderRow)}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-3">
        <Button type="button" variant="ghost" onClick={onBack}>
          Back
        </Button>
        <div className="flex items-center gap-3">
          {!canContinue && (
            <span className="text-xs text-amber-600">
              Map required field{requiredMissing.length === 1 ? '' : 's'}: {requiredMissing.join(', ')}
            </span>
          )}
          <Button type="button" onClick={onContinue} disabled={!canContinue}>
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}
